import { Button } from "@/components/ui/button";
import { Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePosContext } from "../context/pos-context";
import type { PosCartItem } from "../hooks/use-pos-cart";
import { useCustomerInfoCheckout } from "../hooks/use-customer-info-checkout";
import { PosCustomerInfoDialog } from "./pos-customer-info-dialog";
import { PosItemRow } from "./pos-item-row";

function PosCartItemRow({ item }: { item: PosCartItem }) {
  const { cart } = usePosContext();
  const { updateQty, removeItem } = cart;
  return (
    <PosItemRow
      name={item.name}
      imageUrl={item.imageUrl}
      price={item.price}
      quantity={item.quantity}
      onIncrease={() => updateQty(item.variantId, item.quantity + 1)}
      onDecrease={() => updateQty(item.variantId, item.quantity - 1)}
      onRemove={() => removeItem(item.variantId)}
    />
  );
}

export function PosCartView() {
  const { cart, isCheckoutLoading, isDesktop } = usePosContext();
  const {
    isDialogOpen,
    setIsDialogOpen,
    handlePayClick,
    handleCustomerInfoSubmit,
    dialogMode,
    dialogInitialName,
    dialogInitialPhone,
    dialogInitialAddress,
  } = useCustomerInfoCheckout();

  const { items, subtotal } = cart;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const formattedSubtotal = subtotal.toFixed(2);

  return (
    <div
      className={cn(
        "flex flex-col bg-white",
        isDesktop ? "flex-1 min-h-0 overflow-hidden" : "min-h-[100dvh]"
      )}
    >
      <div
        className={cn(
          "flex-1 overflow-y-auto p-3 md:p-4 min-h-0",
          !isDesktop && "pb-[200px]"
        )}
      >
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 md:py-12 text-center">
            <div className="rounded-full bg-gray-100 p-4 md:p-6 mb-3 md:mb-4">
              <Package className="h-10 w-10 md:h-12 md:w-12 text-gray-400" />
            </div>
            <p className="text-muted-foreground font-medium text-sm md:text-base">
              Your cart is empty
            </p>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              Add items from the menu to get started
            </p>
          </div>
        ) : (
          <div className="space-y-2 md:space-y-3">
            {items.map((item) => (
              <PosCartItemRow key={item.variantId} item={item} />
            ))}
          </div>
        )}
      </div>
      <div
        className={
          isDesktop
            ? "border-t bg-white p-3 md:p-4 space-y-2 md:space-y-3 flex-shrink-0 mt-auto"
            : "fixed bottom-0 left-0 right-0 border-t bg-white p-3 md:p-4 space-y-2 md:space-y-3 flex-shrink-0 z-10 safe-area-inset-bottom"
        }
      >
        <div className="space-y-1.5 md:space-y-2">
          <div className="flex justify-between text-xs md:text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium">${formattedSubtotal}</span>
          </div>
          <div className="flex justify-between text-xs md:text-sm">
            <span className="text-muted-foreground">Items</span>
            <span className="font-medium">{itemCount}</span>
          </div>
          <div className="flex justify-between text-base md:text-lg font-bold pt-1.5 md:pt-2 border-t">
            <span>Total</span>
            <span className="text-primary">${formattedSubtotal}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            className="w-full h-11 md:h-12 rounded-xl font-semibold bg-primary hover:bg-primary/90 text-sm md:text-base"
            onClick={handlePayClick}
            disabled={items.length === 0 || isCheckoutLoading}
          >
            {isCheckoutLoading ? "Creating order..." : `Pay $${formattedSubtotal}`}
          </Button>
          <PosCustomerInfoDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            onSubmit={handleCustomerInfoSubmit}
            mode={dialogMode}
            initialName={dialogInitialName}
            initialPhone={dialogInitialPhone}
            initialAddress={dialogInitialAddress}
          />
        </div>
      </div>
    </div>
  );
}
